import axios from 'axios';
import UISession from '@/interfaces/UISession';
import { UI_SESSION_KEY } from '@/constants';

interface User {
    empCode : string;
    accessDept : string | null;
    accessLevel : number | null;
}

interface State {
    token : string;
    expires : Date | null;
    backgroundWorkerURL : string;
    user : User;
}

const expired = (expires : Date | null) => {
  const currentTime = new Date();
  return expires === null || currentTime > expires;
};

const formatSecondsAsDate = (seconds : number) => {
  const timeObject = new Date();
  const milliseconds = seconds * 1000;
  const expires = new Date(timeObject.getTime() + milliseconds);

  return expires;
};

const getFromSessionStorage = async () : Promise<State | null> => {
  const stateAsJson = sessionStorage.getItem(UI_SESSION_KEY);
  if (stateAsJson) {
    const state = JSON.parse(stateAsJson);
    if (!expired(state.expired)) {
      return state;
    }
  }
  return null;
};

const fetchUISession = async (force : boolean = false) => {
  const currentUISession = await getFromSessionStorage();

  if (currentUISession && !force) {
    return currentUISession;
  }

  let sessionDetails = {
    token: '',
    expires: null,
    backgroundWorkerURL: '',
    user: {
      empCode: '',
      accessDept: '',
      accessLevel: null,
    },
  } as State;

  try {
    const { data } = await axios.get('ConnXWebUtils.asmx/GetUiSession', {
      withCredentials: true,
      headers: {
        ContentType: 'application/json',
        Accept: '',
      },
      data: {}, // This annoying little hack, axios will not send content type if no content is set
    });
    const sessionData : UISession = data.d;
    sessionDetails = {
      token: sessionData.ApiToken,
      expires: formatSecondsAsDate(sessionData.ApiTokenExpiresSeconds),
      backgroundWorkerURL: sessionData.ApiUri,
      user: {
        empCode: sessionData.ConnxSessionUser.EmpCode,
        accessDept: sessionData.ConnxSessionUser.AccessDept,
        accessLevel: sessionData.ConnxSessionUser.AccessLevel,
      },
    };
  } catch (error : any) {
    if (process.env.NODE_ENV === 'development') {
      sessionDetails = {
        token: import.meta.env.VITE_APPLICATION_TOKEN,
        expires: formatSecondsAsDate(3600),
        backgroundWorkerURL: import.meta.env.VITE_BACKGROUND_WORKER_URI,
        user: {
          empCode: import.meta.env.VITE_EMP_CODE,
          accessDept: '',
          accessLevel: 3,
        },
      };
    }
  }

  return sessionDetails;
};

export default fetchUISession;
